import {
	changeFilterSelect,
	changePage,
	changeSlice,
	changeSort,
	getLog,
	getLogRejected,
	getLogResolved,
	getRiskConfig,
	getRiskConfigResolved,
	getRiskDetails,
	getRiskDetailsRejected,
	getRiskDetailsResolved,
	reloadRiskDetails,
	resetRisk,
	resetRiskRejected,
	resetRiskResolved,
	runRisk,
	runRiskRejected,
	runRiskResolved,
	setRiskDetailsIsBusy,
	setRiskSetupDirty,
	saveRiskConfigAction,
	createRiskConfig,
	setRiskConfigNameError,
	checkRiskConfigName,
	setRiskConfigNameCheck,
	setRiskConfigIsCreating,
	setRiskIsSaving,
	renameRiskConfigAction,
	deleteRisk,
	setRiskIsDeleting,
	duplicateRiskConfig,
	changeRiskConfigPublishState,
	getRiskCountResolved,
	getRiskCountInspection,
	getRiskCountAttribute,
	getRiskCountDeteriorationModel,
	getRiskCountFailureDefinition,
	getRiskCountProximity,
	getRiskCountProximityAttribute,
	getRiskCountCustomTable,
	setRiskCountIsLoading,
	getSimulationData,
	getSimulationDataResolved,
	getRiskCountScoreMatrix,
	initRiskMatrixPreview,
	setIsPreviewLoading,
	setMatrixPreviewStatus,
	getRiskMatrixPreview,
	setRiskMatrixAfterPreview,
	setDidMatrixUpdate,
	getTaskDataResolved,
	getTaskData,
	setIsPreviewOnLoad,
	getRiskCountObservation,
	getRiskCountTask,
	setEnablePreview,
	setOnlyGradesUpdated,
	cancelRiskMatrixPreview,
	setCancellPreview,
	changePagination,
	changeFiltersSelect,
	clearRiskConfig,
	setSearchStartSaga,
	applyRiskTemplate,
	setTemplateApplyStatus,
	setTemplateApplyResult,
	getRiskConfigCoverage,
	getRiskConfigCoverageResolved,
	getRiskConfigCoverageRejected,
	getRiskAssetCount,
	getRiskAssetCountResolved,
	getRiskAssetCountRejected,
	getRiskResultsSummary,
	getRiskResultsSummaryResolved,
	getRiskResultsSummaryRejected,
	getRiskBreakdown,
	getRiskBreakdownResolved,
	getRiskBreakdownRejected,
} from '@Actions/Risk.actions';

import { error, success } from '@innovyze/stylovyze';
import { put, retry, takeEvery, takeLatest } from '@redux-saga/core/effects';
import { AnyAction, PayloadAction } from '@reduxjs/toolkit';
import { getAssetFilterApi } from '@Services/asset/asset.services';
import {
	saveRiskConfigApi,
	getRiskCalcLogApi,
	getRiskConfigApi,
	getRiskDetailsApi,
	resetRiskCalculationApi,
	startRiskCalculationApi,
	createRiskConfigApi,
	applyRiskTemplateApi,
	deleteRiskApi,
	duplicateRiskConfigApi,
	changeRiskConfigPublishStateApi,
	getRiskCountInspectionApi,
	getRiskCountAtributeApi,
	getRiskCountDeteriorationModelApi,
	getRiskCountFailureDefinitionApi,
	getRiskCountProximityAtributeApi,
	getRiskCountProximityApi,
	getRiskCountCustomTableApi,
	getSimulationDataApi,
	getRiskCountScoreMatrixApi,
	getRiskPreviewApi,
	initRiskPreviewApi,
	getTaskDataApi,
	getRiskCountObservationApi,
	getRiskCountTaskApi,
	resetRiskPreviewCalculationApi,
	getRiskConfigCoverageApi,
	isRiskConfigNameTaken,
	getRiskAssetCountApi,
	getRiskResultsSummaryApi,
	getRiskBreakdownApi,
} from '@Services/Risk/Risk.services';
import {
	AssetFilter,
	Category,
	ComponentCountReply,
	CreateConfig,
	InitRiskMatrixPreviewResponse,
	LogReply,
	RiskConfig,
	RiskRunReply,
	SimulationData,
	TaskData,
} from '@Types';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { select, call, delay } from 'redux-saga/effects';
import { ResolvedResponse, Row } from '@innovyze/lib-am-common';
import { riskDetailsQuerySelector } from '@Selectors';
import { RiskConfig as MongoRiskConfig } from '../types/riskConfig.types';
import { ApplyRiskTemplateResponse } from '@Types/riskTemplateApply.types';
import { CoverageEntry } from '@Types/riskCoverage.types';
import { RiskResultsSummary } from '@Types/riskResultsSummary.types';
import { RiskBreakdownResponse } from '@Types/riskBreakdown.types';
import { describeError } from '@Utils/describeError';
import { t } from 'i18next';
import { getLoFConfigResolved } from '@Actions/LoF.actions';
import { getCoFComponentsResolved } from '@Actions/CoF.actions';
import { addRouteNamespace } from '@Utils/actions';
import { getRiskLandingRoute } from '@Utils/riskTabOrder';
import { withDisplayAssetId } from '@Utils/riskAssetId';
import { defaultRiskMatrix } from '@Reducers/RiskMatrixDefaultScores';
import { setRiskRunning } from '@Actions/util.actions';
import { changeRiskConfigsPage } from '@innovyze/lib-am-common/Actions';
import { assetSchemaSelectorByType } from '@innovyze/lib_asset_schema_store';
import { AssetSchema } from '@innovyze/lib_asset_schema';

export function* hydrateRiskConfigFromMongo(mongoCfg: MongoRiskConfig) {
	// TODO: This should all get re-factored at some point to make the code neater
	const snowRiskConfig = mapMongoCfgToSnowCfg(mongoCfg);

	// Get info about the filter (if available)
	const assetFilterId = mongoCfg?.assetFilterId;
	if (assetFilterId) {
		const assetFilterResponse: AxiosResponse<AssetFilter> = yield call(
			getAssetFilterApi,
			assetFilterId,
		);

		if (assetFilterId === assetFilterResponse.data?._id) {
			snowRiskConfig.assetFilter = assetFilterResponse.data;
		}
	}

	yield put(getRiskConfigResolved(snowRiskConfig));

	const assetSchema: AssetSchema | undefined = yield select(state => {
		return assetSchemaSelectorByType(
			state,
			snowRiskConfig.riskSetting.assetType,
		);
	});

	// get the default categories from asset schema
	const lofDefaultRiskCategories = assetSchema?.getRiskCategories('Lof');
	const defaultCategories: Category[] =
		lofDefaultRiskCategories?.map(({ DisplayName, Condition }) => ({
			name: t(DisplayName),
			isCondition: Condition ?? false,
			useThisOnly: false,
			weighting: 100 / lofDefaultRiskCategories.length,
			useHighestScore: false,
			components: [],
		})) ?? [];

	const snowRiskConfigCategories = snowRiskConfig.LOFCategories;

	let lofConfig;
	if (snowRiskConfigCategories.length) {
		// If there are no default categories, we need to remove the first category
		// as the condition category was added even to the configs that
		// should not have it e.g. WaterDistribution pipe should not have it
		if (
			!defaultCategories.length &&
			snowRiskConfigCategories[0].name === t('Condition Category')
		) {
			lofConfig = snowRiskConfigCategories.slice(1);
		} else {
			lofConfig = snowRiskConfigCategories;
		}
	} else {
		lofConfig = defaultCategories;
	}

	yield put(getLoFConfigResolved(lofConfig));

	yield put(getCoFComponentsResolved(snowRiskConfig.COFCategories));

	return snowRiskConfig;
}

export function* getRiskConfigSaga(action: AnyAction) {
	const configId = action.payload;
	const mongoRiskConfigResponse: AxiosResponse<MongoRiskConfig> = yield call(
		getRiskConfigApi,
		configId,
	);

	yield call(hydrateRiskConfigFromMongo, mongoRiskConfigResponse.data);
}

export const mapMongoCfgToSnowCfg = (mongoCfg: MongoRiskConfig): RiskConfig => {
	const result: RiskConfig = {
		riskSetting: {
			configId: mongoCfg.configId,
			inspectionStandard: mongoCfg.inspectionStandard,
			publishState: mongoCfg.publishState,
			RiskMethod: mongoCfg.riskMethod,
			COFWeighting: mongoCfg.cofWeighting,
			LOFWeighting: mongoCfg.lofWeighting,
			LastRun: mongoCfg.lastRun,
			// Use the lastRun if the completed date isn't there for older runs
			lastRunCompleted: mongoCfg.lastRunCompleted ?? mongoCfg.lastRun,
			// Raw signal, before the LastRun fallback above: the backend only sets
			// lastRunCompleted on success and keeps it through later failed or
			// cancelled re-runs, whereas `status` is the latest run's status.
			// Older runs predate lastRunCompleted, so also accept a latest run
			// that completed (not merely `lastRun`, which is set at run start).
			hasCompletedRun:
				!!mongoCfg.lastRunCompleted ||
				mongoCfg.status === 'risk-calc-complete',
			Grades: {
				Low: mongoCfg.grades.low,
				Medium: mongoCfg.grades.medium,
				High: mongoCfg.grades.high,
				Max: mongoCfg.grades.max,
				Extreme: mongoCfg.grades.extreme,
			},
			assetType: mongoCfg.assetType,
			assetFilterId: mongoCfg.assetFilterId,
			name: mongoCfg.name,
			assetCount: mongoCfg.assetCount,
			runBy: mongoCfg.runBy,
			status: mongoCfg.status,
			riskType: mongoCfg.riskType ?? 'linear',
			matrix: mongoCfg.matrix ?? { ...defaultRiskMatrix },
			maxOverallRiskScore: mongoCfg.maxOverallRiskScore,
			postProcessing: mongoCfg.postProcessing,
			...(mongoCfg.templateId && { templateId: mongoCfg.templateId }),
			...(mongoCfg.templateVersion !== undefined && {
				templateVersion: mongoCfg.templateVersion,
			}),
		},
		LOFCategories: mongoCfg.lofCfgCategories,
		COFCategories: mongoCfg.cofCfgCategories,
	};

	return result;
};

export const mapSnowCfgToMongoCfg = (snowCfg: RiskConfig): MongoRiskConfig => {
	const result: MongoRiskConfig = {
		publishState: snowCfg.riskSetting.publishState,
		inspectionStandard: snowCfg.riskSetting.inspectionStandard,
		configId: snowCfg.riskSetting.configId,
		name: snowCfg.riskSetting.name,
		cofWeighting: snowCfg.riskSetting.COFWeighting,
		lofWeighting: snowCfg.riskSetting.LOFWeighting,
		riskMethod: snowCfg.riskSetting.RiskMethod,
		lastRun: snowCfg.riskSetting.LastRun,
		grades: {
			low: snowCfg.riskSetting.Grades.Low,
			medium: snowCfg.riskSetting.Grades.Medium,
			high: snowCfg.riskSetting.Grades.High,
			extreme: snowCfg.riskSetting.Grades.Extreme,
			max: snowCfg.riskSetting.Grades.Max,
		},
		assetType: snowCfg.riskSetting.assetType,
		assetFilterId: snowCfg.riskSetting.assetFilterId,
		assetCount: snowCfg.riskSetting.assetCount,
		runBy: snowCfg.riskSetting.runBy,
		status: snowCfg.riskSetting.status,

		lofCfgCategories: snowCfg.LOFCategories.map(cat => ({
			...cat,
			components: cat.components.map(comp => ({
				...comp,
				inspectionType: 'PACP',
			})),
		})),

		cofCfgCategories: snowCfg.COFCategories,
		riskType: snowCfg.riskSetting.riskType ?? 'linear',
		matrix: snowCfg.riskSetting.matrix ?? { ...defaultRiskMatrix },
		maxOverallRiskScore: snowCfg.riskSetting.maxOverallRiskScore,
		postProcessing: snowCfg.riskSetting.postProcessing,
	};

	return result;
};

function* watchGetRiskConfig() {
	yield takeLatest(getRiskConfig, getRiskConfigSaga);
}

interface ConfigData {
	errorMessage: string;
}

export function* createRiskConfigSaga(action: AnyAction) {
	try {
		const config: CreateConfig = {
			...action.payload.config,
			publishState: 'unpublished',
		};

		const response: AxiosResponse = yield call(createRiskConfigApi, config);
		yield put(setRiskConfigNameError(t('')));

		const landingRoute = action.payload.isRiskTemplatesEnabled
			? getRiskLandingRoute(false)
			: 'results';
		action.payload.history.push(
			addRouteNamespace(`${landingRoute}/${response.data.insertedId}`),
		);
		yield put(setRiskConfigIsCreating(false));
		yield put(changeRiskConfigsPage(action.payload.page));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			const data = axiosError.response?.data as ConfigData;
			if (data?.errorMessage === '[400] Duplicate config name') {
				yield put(setRiskConfigNameError(t('Enter a unique name')));
			} else {
				yield put(
					error(
						t('Failed to create risk model: {{info}}', {
							info:
								axiosError.response?.statusText ??
								data?.errorMessage ??
								t('No additional information'),
						}),
					),
				);
			}
		} else {
			yield put(
				error(
					t('Failed to create risk model: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setRiskConfigIsCreating(false));
	}
}

export function* duplicateRiskConfigSaga(action: AnyAction) {
	try {
		const response: AxiosResponse = yield call(
			duplicateRiskConfigApi,
			action.payload.configId,
			action.payload.config,
		);
		yield put(setRiskConfigIsCreating(false));
		const { configId } = response.data;
		if (configId) {
			yield put(clearRiskConfig());
			const landingRoute = action.payload.isRiskTemplatesEnabled
				? getRiskLandingRoute(false)
				: 'results';
			action.payload.history.push(
				addRouteNamespace(`${landingRoute}/${configId}`),
			);
			yield put(changeRiskConfigsPage(1));
		}
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			const data = axiosError.response?.data as ConfigData;
			yield put(
				error(
					t('Failed to create risk model: {{info}}', {
						info:
							axiosError.response?.statusText ??
							data?.errorMessage ??
							t('No additional information'),
					}),
				),
			);
		} else {
			yield put(
				error(
					t('Failed to create risk model: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setRiskConfigIsCreating(false));
	}
}

function* watchCreateRiskConfig() {
	yield takeLatest(duplicateRiskConfig, duplicateRiskConfigSaga);
	yield takeLatest(createRiskConfig, createRiskConfigSaga);
}

export function* applyRiskTemplateSaga(
	action: ReturnType<typeof applyRiskTemplate>,
) {
	try {
		yield put(setTemplateApplyStatus('applying'));

		const response: AxiosResponse<ApplyRiskTemplateResponse> = yield call(
			applyRiskTemplateApi,
			action.payload.config,
		);

		yield call(hydrateRiskConfigFromMongo, response.data.config);

		yield put(
			setTemplateApplyResult({
				availability: response.data.availability ?? [],
				unrecognisedValues: response.data.unrecognisedValues ?? [],
				templateLabel: action.payload.templateLabel,
			}),
		);

		action.payload.history.push(
			addRouteNamespace(`lofSetup/${response.data.config.configId}`),
		);
		yield put(setRiskConfigIsCreating(false));
		yield put(changeRiskConfigsPage(action.payload.page));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			const data = axiosError.response?.data as ConfigData;
			if (data?.errorMessage === '[400] Duplicate config name') {
				yield put(setRiskConfigNameError(t('Enter a unique name')));
			} else {
				yield put(
					error(
						t('Failed to apply risk template: {{info}}', {
							info:
								axiosError.response?.statusText ??
								data?.errorMessage ??
								t('No additional information'),
						}),
					),
				);
			}
		} else {
			yield put(
				error(
					t('Failed to apply risk template: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setTemplateApplyStatus('error'));
		yield put(setRiskConfigIsCreating(false));
	}
}

function* watchApplyRiskTemplate() {
	yield takeLatest(applyRiskTemplate, applyRiskTemplateSaga);
}

// The new-model dialog waits on riskConfigNameCheck and only moves to step 2
// when the name is free.
export function* checkRiskConfigNameSaga(
	action: ReturnType<typeof checkRiskConfigName>,
) {
	try {
		const isTaken: boolean = yield call(
			isRiskConfigNameTaken,
			action.payload,
		);
		if (isTaken) {
			yield put(setRiskConfigNameError(t('Enter a unique name')));
			yield put(setRiskConfigNameCheck('taken'));
		} else {
			yield put(setRiskConfigNameCheck('free'));
		}
	} catch {
		// a toast rather than a field error, so Continue stays enabled to retry
		yield put(error(t('Could not check the name. Try again.')));
		yield put(setRiskConfigNameCheck('failed'));
	}
}

function* watchCheckRiskConfigName() {
	yield takeLatest(checkRiskConfigName, checkRiskConfigNameSaga);
}

export function* getRiskConfigCoverageSaga(
	action: ReturnType<typeof getRiskConfigCoverage>,
) {
	try {
		const response: AxiosResponse<CoverageEntry[]> = yield call(
			getRiskConfigCoverageApi,
			action.payload,
		);
		yield put(getRiskConfigCoverageResolved(response.data));
	} catch (e) {
		// No toast: a missing pill degrades gracefully, but the failure should
		// still be diagnosable.
		console.warn('Failed to load risk config coverage', {
			configId: action.payload,
			error: describeError(e),
		});
		yield put(getRiskConfigCoverageRejected());
	}
}

function* watchGetRiskConfigCoverage() {
	yield takeLatest(getRiskConfigCoverage, getRiskConfigCoverageSaga);
}

export function* getRiskAssetCountSaga(
	action: ReturnType<typeof getRiskAssetCount>,
) {
	try {
		const response: AxiosResponse<{ assetCount: number }> = yield call(
			getRiskAssetCountApi,
			action.payload.assetType,
			action.payload.assetFilterId,
		);
		yield put(getRiskAssetCountResolved(response.data.assetCount));
	} catch (e) {
		// No toast: the summary hides the number, but keep it diagnosable.
		console.warn('Failed to load risk assets-in-scope count', {
			...action.payload,
			error: describeError(e),
		});
		yield put(getRiskAssetCountRejected());
	}
}

export function* watchGetRiskAssetCount() {
	yield takeLatest(getRiskAssetCount, getRiskAssetCountSaga);
}

export function* getRiskResultsSummarySaga(
	action: ReturnType<typeof getRiskResultsSummary>,
) {
	const configId = action.payload;
	try {
		const response: AxiosResponse<RiskResultsSummary> = yield call(
			getRiskResultsSummaryApi,
			configId,
		);
		yield put(
			getRiskResultsSummaryResolved({ configId, data: response.data }),
		);
	} catch (e) {
		// No toast: the KPI area shows its own retry message, and the grid
		// below is unaffected.
		console.warn('Failed to load risk results summary', {
			configId,
			error: describeError(e),
		});
		yield put(getRiskResultsSummaryRejected(configId));
	}
}

function* watchGetRiskResultsSummary() {
	yield takeLatest(getRiskResultsSummary, getRiskResultsSummarySaga);
}

export function* getRiskBreakdownSaga(
	action: ReturnType<typeof getRiskBreakdown>,
) {
	const { configId, assetId, requestId } = action.payload;
	try {
		const response: AxiosResponse<RiskBreakdownResponse> = yield call(
			getRiskBreakdownApi,
			configId,
			assetId,
		);
		yield put(
			getRiskBreakdownResolved({
				configId,
				assetId,
				requestId,
				data: response.data,
			}),
		);
	} catch (e) {
		// No toast: the panel shows its own retry message.
		console.warn('Failed to load risk breakdown', {
			configId,
			assetId,
			error: describeError(e),
		});
		yield put(getRiskBreakdownRejected({ configId, assetId, requestId }));
	}
}

// takeEvery, not takeLatest: each expanded row is its own request, and
// opening a second row must not cancel the first.
export function* watchGetRiskBreakdown() {
	yield takeEvery(getRiskBreakdown, getRiskBreakdownSaga);
}

export function* renameRiskConfigSaga(action: PayloadAction<RiskConfig>) {
	try {
		yield put(setRiskIsSaving(true));

		const snowRiskConfig = action.payload;
		const mongoRiskConfig = mapSnowCfgToMongoCfg(snowRiskConfig);

		yield call(saveRiskConfigApi, mongoRiskConfig);
		yield put(success(t('Risk model renamed')));
		yield put(getRiskConfigResolved(snowRiskConfig));
		yield put(setRiskIsSaving(false));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			const data = axiosError.response?.data as ConfigData;
			if (data?.errorMessage === '[400] Duplicate config name') {
				yield put(setRiskConfigNameError(t('Enter a unique name')));
			} else {
				yield put(
					error(
						t('Risk model failed to save: {{info}}', {
							info:
								axiosError.response?.statusText ??
								data?.errorMessage ??
								t('No additional information'),
						}),
					),
				);
			}
		} else {
			yield put(
				error(
					t('Risk model failed to save: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setRiskIsSaving(false));
	}
}

export function* saveRiskConfigSaga(action: PayloadAction<RiskConfig>) {
	try {
		yield put(setRiskIsSaving(true));

		const snowRiskConfig = action.payload;
		const mongoRiskConfig = mapSnowCfgToMongoCfg(snowRiskConfig);

		yield call(saveRiskConfigApi, mongoRiskConfig);
		yield put(success(t('Risk model saved')));
		yield put(setRiskSetupDirty(false));
		yield put(setRiskIsSaving(false));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			const data = axiosError.response?.data as ConfigData;
			if (data?.errorMessage === '[400] Duplicate config name') {
				yield put(setRiskConfigNameError(t('Enter a unique name')));
			} else {
				yield put(
					error(
						t('Risk model failed to save: {{info}}', {
							info:
								axiosError.response?.statusText ??
								data?.errorMessage ??
								t('No additional information'),
						}),
					),
				);
			}
		} else {
			yield put(
				error(
					t('Risk model failed to save: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setRiskIsSaving(false));
	}
}

function* watchStoreRiskConfig() {
	yield takeLatest(renameRiskConfigAction, renameRiskConfigSaga);
	yield takeLatest(saveRiskConfigAction, saveRiskConfigSaga);
}

export function* getRiskDetailsSaga() {
	try {
		yield put(setRiskDetailsIsBusy(true));
		const query: string = yield select(riskDetailsQuerySelector);
		const payload: AxiosResponse<ResolvedResponse> = yield retry(
			2,
			1500,
			getRiskDetailsApi,
			query,
		);
		const rows = payload.data.data as Row[];
		const data = {
			...payload.data,
			data: rows.map(riskDetail =>
				withDisplayAssetId(riskDetail as Row & { ASSET_ID: string }),
			),
		};
		yield put(getRiskDetailsResolved(data));
		yield put(setRiskDetailsIsBusy(false));
		yield put(setSearchStartSaga(false));
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			yield put(error(t('Failed to get risk details')));
			yield put(
				getRiskDetailsRejected({
					code: axiosError.response?.status ?? -1,
					text:
						axiosError.response?.statusText ??
						t('No additional information'),
				}),
			);
		}
		yield put(setRiskDetailsIsBusy(false));
		yield put(setSearchStartSaga(false));
	}
}
function* watchGetRiskDetails() {
	yield takeLatest(
		[
			getRiskDetails,
			reloadRiskDetails,
			changePage,
			changeSlice,
			changePagination,
			changeSort,
			changeFilterSelect,
			changeFiltersSelect,
		],
		getRiskDetailsSaga,
	);
}

export function* getLogSaga(action: AnyAction) {
	try {
		const configId: string = action.payload;
		const response: AxiosResponse<LogReply> = yield retry(
			5,
			1500,
			getRiskCalcLogApi,
			configId,
		);
		yield put(getLogResolved(response.data));
	} catch (e) {
		yield put(getLogRejected());
	}
}
function* watchGetLog() {
	yield takeLatest(getLog, getLogSaga);
}

export function* runRiskSaga(action: AnyAction) {
	try {
		const configId: string = action.payload;
		const riskSettingResponse: AxiosResponse<RiskRunReply> = yield retry(
			1, // Take this down to 1 retry for the 404 fix
			1500,
			startRiskCalculationApi,
			configId,
		);
		const reply = riskSettingResponse.data;
		yield put(runRiskResolved());
		switch (reply.calcStatus) {
			case 'risk-calc-in-progress':
			case 'risk-calc-post-processing-normalize':
				yield put(error(t('Risk calculation already in progress')));
				break;
			default:
				yield put(success(t('Risk calculation requested')));
				break;
		}
	} catch (e) {
		yield put(runRiskRejected());
	}
}
function* watchRunRisk() {
	yield takeLatest(runRisk, runRiskSaga);
}

export function* resetRiskSaga(action: AnyAction) {
	try {
		const configId: string = action.payload;
		yield retry(
			1, // Take this down to 1 retry for the 404 fix
			1500,
			resetRiskCalculationApi,
			configId,
		);

		yield put(setRiskRunning(false));
		yield put(resetRiskResolved());
		yield put(success(t('Risk calculation cancel requested')));
	} catch (e) {
		yield put(setRiskRunning(false));
		yield put(resetRiskRejected());
		yield put(success(t('Risk calculation failed')));
	}
}
function* watchResetRisk() {
	yield takeLatest(resetRisk, resetRiskSaga);
}

export function* deleteRiskSaga(action: AnyAction) {
	try {
		yield retry(3, 1500, deleteRiskApi, action.payload.configId);
		yield put(success(t('Risk model deleted successfully')));
		yield put(setRiskIsDeleting(false));
		action.payload.refresh();
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const axiosError = e as AxiosError;
			yield put(
				error(
					t('Risk model failed to delete: {{info}}', {
						info:
							axiosError.response?.statusText ??
							t('No additional information'),
					}),
				),
			);
		} else {
			yield put(
				error(
					t('Risk model failed to delete: {{info}}', {
						info: t('No additional information'),
					}),
				),
			);
		}
		yield put(setRiskIsDeleting(false));
	}
}
function* watchDeleteRiskeSaga() {
	yield takeLatest(deleteRisk, deleteRiskSaga);
}

export function* changeRiskConfigPublishStateSaga(action: AnyAction) {
	const { newPublishState, configId } = action.payload;
	try {
		yield call(changeRiskConfigPublishStateApi, newPublishState, configId);
		yield put(
			success(t('Risk model {{newPublishState}}', { newPublishState })),
		);
		action.payload.refresh();
	} catch (err) {
		yield put(
			error(
				t('Failed to {{newPublishState}} risk model', {
					newPublishState,
				}),
			),
		);
	}
}
function* watchChangeRiskConfigPublishStateSaga() {
	yield takeLatest(
		changeRiskConfigPublishState,
		changeRiskConfigPublishStateSaga,
	);
}

export function* getRiskCountInspectionSaga(action: AnyAction) {
	const {
		key,
		metadata,
		metadataType,
		assetType,
		assetFilterId,
		fieldName,
		inspectionType,
	} = action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountInspectionApi,
			metadata,
			metadataType,
			assetType,
			assetFilterId,
			fieldName,
			inspectionType,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

export function* getRiskCountAttributeSaga(action: AnyAction) {
	const { key, metadata, metadataType, assetType, assetFilterId, fieldName } =
		action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountAtributeApi,
			metadata,
			metadataType,
			assetType,
			assetFilterId,
			fieldName,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

export function* getRiskCountDeteriorationModelSaga(action: AnyAction) {
	const {
		key,
		metadata,
		metadataType,
		assetType,
		assetFilterId,
		fieldName,
		deteriorationModelId,
	} = action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountDeteriorationModelApi,
			metadata,
			metadataType,
			assetType,
			assetFilterId,
			fieldName,
			deteriorationModelId,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

export function* getRiskCountFailureDefinitionSaga(action: AnyAction) {
	const {
		key,
		metadata,
		metadataType,
		assetType,
		assetFilterId,
		fieldName,
		failureDefinitionId,
	} = action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountFailureDefinitionApi,
			metadata,
			metadataType,
			assetType,
			assetFilterId,
			fieldName,
			failureDefinitionId,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

export function* getRiskCountObservationSaga(action: AnyAction) {
	const { key, metadata, metadataType, assetType, assetFilterId, fieldName } =
		action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountObservationApi,
			metadata,
			metadataType,
			assetType,
			assetFilterId,
			fieldName,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}
export function* getRiskCountProximitySaga(action: AnyAction) {
	const { key, metadata, metadataType, assetType, assetFilterId, layerName } =
		action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountProximityApi,
			metadata,
			metadataType,
			assetType,
			assetFilterId,
			layerName,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

export function* getRiskCountProximityAttributeSaga(action: AnyAction) {
	const {
		key,
		metadata,
		metadataType,
		assetType,
		assetFilterId,
		layerName,
		fieldName,
		valueType,
		distance,
	} = action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountProximityAtributeApi,
			metadata,
			metadataType,
			assetType,
			assetFilterId,
			layerName,
			fieldName,
			valueType,
			distance,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

export function* getSimulationDataSaga() {
	const response: AxiosResponse<SimulationData> = yield retry(
		5,
		1500,
		getSimulationDataApi,
	);
	yield put(getSimulationDataResolved(response.data));
}

function* watchGetSimulationData() {
	yield takeLatest(getSimulationData, getSimulationDataSaga);
}

export function* getTaskDataSaga(action: AnyAction) {
	const assetType = action.payload;

	const response: AxiosResponse<TaskData> = yield retry(
		5,
		1500,
		getTaskDataApi,
		assetType,
	);
	yield put(getTaskDataResolved(response.data));
}

function* watchGetTaskData() {
	yield takeLatest(getTaskData, getTaskDataSaga);
}

export function* getRiskCountCustomTableSaga(action: AnyAction) {
	const {
		key,
		metadata,
		metadataType,
		assetType,
		assetFilterId,
		fieldName,
		tableName,
		valueType,
	} = action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountCustomTableApi,
			metadata,
			metadataType,
			assetType,
			assetFilterId,
			fieldName,
			tableName,
			valueType,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

export function* getRiskCountTaskSaga(action: AnyAction) {
	const {
		key,
		metadata,
		metadataType,
		assetType,
		assetFilterId,
		fieldName,
		tableName,
		valueType,
		inspectionType,
	} = action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountTaskApi,
			metadata,
			metadataType,
			assetType,
			assetFilterId,
			fieldName,
			tableName,
			valueType,
			inspectionType,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

export function* getRiskCountScoreMatrixSaga(action: AnyAction) {
	const {
		key,
		components,
		scoreMatrix,
		assetType,
		assetFilterId,
		inspectionType,
	} = action.payload;
	try {
		yield put(getRiskCountResolved({ key: key, values: undefined }));
		yield put(setRiskCountIsLoading({ key: key, loading: true }));

		const response: AxiosResponse<ComponentCountReply[]> = yield call(
			getRiskCountScoreMatrixApi,
			components,
			scoreMatrix,
			assetType,
			assetFilterId,
			inspectionType,
		);
		yield put(
			getRiskCountResolved({
				key: key,
				values: response.data,
			}),
		);
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	} catch (err) {
		yield put(setRiskCountIsLoading({ key: key, loading: false }));
	}
}

function* watchGetRiskCount() {
	yield takeLatest(getRiskCountInspection, getRiskCountInspectionSaga);
	yield takeLatest(getRiskCountAttribute, getRiskCountAttributeSaga);
	yield takeLatest(
		getRiskCountDeteriorationModel,
		getRiskCountDeteriorationModelSaga,
	);
	yield takeLatest(
		getRiskCountFailureDefinition,
		getRiskCountFailureDefinitionSaga,
	);
	yield takeLatest(getRiskCountProximity, getRiskCountProximitySaga);
	yield takeLatest(getRiskCountObservation, getRiskCountObservationSaga);

	yield takeLatest(
		getRiskCountProximityAttribute,
		getRiskCountProximityAttributeSaga,
	);
	yield takeLatest(getRiskCountCustomTable, getRiskCountCustomTableSaga);
	yield takeLatest(getRiskCountTask, getRiskCountTaskSaga);
	yield takeLatest(getRiskCountScoreMatrix, getRiskCountScoreMatrixSaga);
}

export function* initRiskMatrixPreviewSaga(action: AnyAction) {
	const {
		cofWeighting,
		lofWeighting,
		riskMethod,
		grades,
		grid,
		preview,
		configId,
		initialLoad,
	} = action.payload;
	try {
		yield put(setCancellPreview(false));
		yield put(setIsPreviewLoading(true));
		yield put(setIsPreviewOnLoad(initialLoad));
		yield put(setMatrixPreviewStatus('started'));
		const previewPostresponse: AxiosResponse<InitRiskMatrixPreviewResponse> =
			yield call(
				initRiskPreviewApi,
				cofWeighting,
				lofWeighting,
				riskMethod,
				grades,
				grid,
				preview,
				configId,
			);
		if (
			previewPostresponse?.data &&
			[
				'risk-calc-matrix-preview-start',
				'risk-calc-matrix-preview-in-progress',
				'risk-calc-matrix-preview-initiated',
			].includes(previewPostresponse?.data?.calcStatus)
		) {
			yield put(success(t('Risk Preview is being calculated.')));
			// This is needed so that the config has time to set its status to start.
			// Otherwise, when polling immediately, its in a previous completed state.
			yield delay(1000);
			yield put(getRiskMatrixPreview({ configId, initialLoad }));
		}
	} catch (err) {
		yield put(setCancellPreview(false));
		yield put(setEnablePreview(false));
		yield put(setIsPreviewLoading(false));
		yield put(setOnlyGradesUpdated(false));
		yield put(setDidMatrixUpdate(false));
		yield put(error(t('Preview calculation failed')));
	}
}

export function* cancelRiskMatrixPreviewSaga(action: AnyAction) {
	const { configId, matrix } = action.payload;
	// Use the configId to cancel the request on the server
	yield call(resetRiskPreviewCalculationApi, configId);

	// Set the results to what the preview was asked for
	yield put(setRiskMatrixAfterPreview(matrix));
	yield put(setCancellPreview(true));
	yield put(setEnablePreview(false));
	yield put(setIsPreviewLoading(false));
	yield put(setOnlyGradesUpdated(false));
	yield put(setDidMatrixUpdate(false));
	yield put(error(t('Preview calculation cancelled')));
}

export function* getRiskMatrixPreviewSaga(action: AnyAction) {
	const { configId, initialLoad } = action.payload;
	try {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const getPreviewResponse: AxiosResponse<any> = yield call(
			getRiskPreviewApi,
			configId,
		);

		if (getPreviewResponse?.data?.calcStatus) {
			yield put(
				setMatrixPreviewStatus(getPreviewResponse?.data?.calcStatus),
			);
			if (
				getPreviewResponse?.data?.calcStatus ===
				'risk-calc-matrix-preview-failed'
			) {
				yield put(setEnablePreview(false));
				yield put(setIsPreviewLoading(false));
				yield put(setOnlyGradesUpdated(false));
				yield put(setDidMatrixUpdate(false));
				yield put(setRiskSetupDirty(false));
				yield put(error(t('Preview calculation failed')));
			}
			if (
				getPreviewResponse?.data?.calcStatus ===
				'risk-calc-matrix-preview-complete'
			) {
				yield put(setCancellPreview(false));
				yield put(setEnablePreview(false));
				yield put(setIsPreviewLoading(false));
				if (
					getPreviewResponse?.data?.preview?.grades?.cofRanges
						?.length > 0 &&
					getPreviewResponse?.data?.preview?.grades?.lofRanges
						?.length > 0 &&
					getPreviewResponse?.data?.preview?.grid?.length > 0
				) {
					yield put(
						setRiskMatrixAfterPreview(
							getPreviewResponse.data.preview,
						),
					);
					yield put(setOnlyGradesUpdated(false));
					yield put(setDidMatrixUpdate(true));
					if (!initialLoad) yield put(setRiskSetupDirty(true));
				} else {
					yield put(setOnlyGradesUpdated(false));
					yield put(setDidMatrixUpdate(false));
					if (!initialLoad) yield put(setRiskSetupDirty(true));
					yield put(error(t('Preview calculation failed')));
				}
			}
		}
	} catch (err) {
		yield put(setCancellPreview(false));
		yield put(setEnablePreview(false));
		yield put(setIsPreviewLoading(false));
		yield put(setOnlyGradesUpdated(false));
		yield put(setDidMatrixUpdate(false));
		if (!initialLoad) yield put(setRiskSetupDirty(true));
		yield put(error(t('Preview calculation failed')));
	}
}

function* watchGetRiskMatrix() {
	yield takeLatest(initRiskMatrixPreview, initRiskMatrixPreviewSaga);
	yield takeLatest(cancelRiskMatrixPreview, cancelRiskMatrixPreviewSaga);
	yield takeLatest(getRiskMatrixPreview, getRiskMatrixPreviewSaga);
}

const sagaArray = [
	watchRunRisk(),
	watchCreateRiskConfig(),
	watchApplyRiskTemplate(),
	watchCheckRiskConfigName(),
	watchGetRiskConfigCoverage(),
	watchGetRiskAssetCount(),
	watchGetRiskResultsSummary(),
	watchGetRiskBreakdown(),
	watchGetRiskConfig(),
	watchStoreRiskConfig(),
	watchGetRiskDetails(),
	watchGetLog(),
	watchResetRisk(),
	watchDeleteRiskeSaga(),
	watchChangeRiskConfigPublishStateSaga(),
	watchGetRiskCount(),
	watchGetRiskMatrix(),
	watchGetSimulationData(),
	watchGetTaskData(),
];

export default sagaArray;
